import { Formik } from "formik";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { serializeErrorKeyValues } from "../../api/network/errorCodes";
import {
  useEditSharedReportViaEmailMutation,
  useGetReportSpecificFieldsQuery,
  useSharedReportViaEmailMutation,
} from "../../api/network/reportApiServices";
import { setModalsData, TModalsState } from "../../api/store/commonSlice";
import {
  SharedReportViaEmailPayload,
  ShareReportListItem,
} from "../../api/types/Report";
import {
  AdminFormFieldCheckbox,
  AdminFormFieldDropdown,
  AdminFormFieldInput,
  AdminFormFieldPillItem,
  AdminFormFieldPillList,
  AdminFormFieldRadio,
  AdminFormFieldSubmit,
  PillItem,
} from "../../components/admin/formFields";
import { APP_CONFIG } from "../../constants/constants";
import { datepickerValueToUTC, useAccountTimezone } from "../../hooks/timezone";
import { localStorageKeys } from "../../utils/localStorageItems";
import {
  DAY_OF_MONTH_OPTIONS,
  DAY_OF_WEEK_OPTIONS,
  FREQUENCY_OPTIONS,
  getDayOfWeekInNumber,
} from "./constants";
import { useReportUtils } from "./hooks/reportUtils";
import {
  shareViaEmailInitialValues,
  shareViaEmailValidationSchema,
  TDownloadType,
  TSendAutomaticallyPeriod,
} from "./validation";
type ModalShareViaEmailProps = {
  reportType?: string;
  reportId?: number;
  isEdit?: boolean;
  sharedReportItem?: ShareReportListItem;
  columns?: any[];
  onClose?: () => void;
  isSummaryDependent?: boolean;
  downloadFormats?: string[];
};

const ModalShareViaEmail: FC<ModalShareViaEmailProps> = ({
  reportType,
  reportId,
  isEdit,
  sharedReportItem,
  columns = [],
  onClose,
  downloadFormats,
}) => {
  const { t: tMain } = useTranslation();
  const { t } = useTranslation("translation", {
    keyPrefix: "reports.shareViaEmail",
  });
  const dispatch = useDispatch();
  const { t: tReports } = useTranslation("translation", {
    keyPrefix: "reports",
  });
  const modalsState: TModalsState = useSelector(
    (state: any) => state.commonReducer.modals
  );
  // share report via email mutation
  const [editSharedReportViaEmailAPITrigger] =
    useEditSharedReportViaEmailMutation();
  const [sharedReportViaEmailAPITrigger] = useSharedReportViaEmailMutation();

  const { refetchCustomReportListToStore } = useReportUtils();

  const triggerAPI = isEdit
    ? editSharedReportViaEmailAPITrigger
    : sharedReportViaEmailAPITrigger;

  const hideModal = () => {
    dispatch(
      setModalsData({ ...modalsState, showShareViaEmailReportModal: false })
    );
    if (onClose) {
      onClose();
    }
  };
  const { accountTzForDisplay } = useAccountTimezone();
  const { data: reportDetails } = useGetReportSpecificFieldsQuery(
    {
      type: reportType as any,
      report_id: reportId as number,
    },
    {
      skip: !reportId || !reportType,
    }
  );
  const reportDownloadFormats =
    downloadFormats ?? reportDetails?.download_formats;
  const allowsXls =
    !reportDownloadFormats || reportDownloadFormats.includes("xlsx");
  // A share saved as XLS before this report stopped offering it is sent as CSV.
  const format =
    sharedReportItem?.external_share?.format === 1
      ? allowsXls
        ? "xls"
        : "csv"
      : sharedReportItem?.external_share?.format === 2
        ? "csv"
        : ("pdf" as TDownloadType);
  const shareEditValues = {
    selected_emails: [sharedReportItem?.email] as string[],
    email: "",
    download_type: format,
    send_automatically: sharedReportItem?.external_share?.send_automatically,
    send_automatically_period:
      !!sharedReportItem?.external_share?.frequency &&
      (FREQUENCY_OPTIONS[
        sharedReportItem?.external_share?.frequency - 1
      ] as TSendAutomaticallyPeriod),
    day_of_month:
      sharedReportItem?.external_share?.day_of_the_month?.toString() || "",
    day_of_month2:
      sharedReportItem?.external_share?.day_of_the_month?.toString() || "",
    day_of_week:
      sharedReportItem?.external_share?.day_of_the_month?.toString() || "",
    delay_time: sharedReportItem?.external_share?.time_of_the_day || "",
  };

  if (modalsState.showShareViaEmailReportModal === false) return null;
  return (
    <div className="justify-center items-start flex overflow-x-hidden overflow-y-auto fixed inset-0 z-overlay outline-none focus:outline-none">
      <div
        className="fixed w-full h-screen bg-modal-overlay z-overlay"
        onClick={hideModal}
      ></div>
      <div className="relative my-6 mx-auto max-w-[calc(100vw-4rem)] w-[600px] z-modal">
        <Formik
          initialValues={
            isEdit
              ? shareEditValues
              : {
                  ...shareViaEmailInitialValues,
                  download_type: allowsXls
                    ? shareViaEmailInitialValues.download_type
                    : ("csv" as TDownloadType),
                }
          }
          validationSchema={shareViaEmailValidationSchema}
          onSubmit={(values, { setSubmitting }) => {
            const payload: SharedReportViaEmailPayload = {
              report_external_share_emails: values.selected_emails.map(
                (email: string) => ({ email: email })
              ),
              format:
                values.download_type === "xls"
                  ? 1
                  : values.download_type === "csv"
                    ? 2
                    : 3,
              send_automatically: values.send_automatically ? 1 : 0,
            };

            if (values.send_automatically) {
              switch (values.send_automatically_period) {
                case "hourly":
                  payload.frequency = 1;
                  break;
                case "daily":
                  payload.frequency = 2;
                  payload.time_of_the_day = values.delay_time;
                  break;
                case "weekly":
                  payload.frequency = 3;
                  payload.day_of_the_week = getDayOfWeekInNumber(
                    values.day_of_week
                  );
                  payload.time_of_the_day = values.delay_time;
                  break;
                case "monthly":
                  payload.frequency = 4;
                  payload.day_of_the_month = parseInt(values.day_of_month);
                  payload.time_of_the_day = values.delay_time;
                  break;
                case "bi_monthly":
                  payload.frequency = 5;
                  payload.day_of_the_month = parseInt(values.day_of_month);
                  payload.day_of_the_month2 = parseInt(values.day_of_month2);
                  payload.time_of_the_day = values.delay_time;
                  break;
              }
            }
            if (isEdit) {
              payload.externalShareId = sharedReportItem?.external_share?.id;
            } else {
              payload.report = reportId;
            }
            // Check if report is summary dependent and time is before summary process complete time
            const shouldShowEarlyTimeMessage = () => {
              if (
                !reportDetails?.is_summary_dependent ||
                !values.send_automatically ||
                !values.delay_time
              )
                return false;
              try {
                const summaryProcessCompleteTimeUtc = localStorage.getItem(
                  localStorageKeys.summaryProcessCompleteTimeUtc
                );
                console.log(
                  "Dynamic summary complete time from backend:",
                  summaryProcessCompleteTimeUtc
                );
                if (!summaryProcessCompleteTimeUtc) return false;
                const timeParts = summaryProcessCompleteTimeUtc
                  .replace("Z", "")
                  .split(":");
                const summaryCompleteMinutes =
                  parseInt(timeParts[0]) * 60 + parseInt(timeParts[1]);
                const accountTimezone =
                  accountTzForDisplay || APP_CONFIG.DEFAULT_TIMEZONE;
                const today = new Date().toISOString().split("T")[0];
                const utcDate = new Date(
                  datepickerValueToUTC(
                    `${today}T${values.delay_time}`,
                    accountTimezone
                  ) * 1000
                );
                return (
                  utcDate.getUTCHours() * 60 + utcDate.getUTCMinutes() <
                  summaryCompleteMinutes
                );
              } catch {
                return false;
              }
            };
            triggerAPI(payload)
              .unwrap()
              .then((data) => {
                if (!!data?.id) {
                  // changes the checkcase condition
                  dispatch(
                    setModalsData({
                      ...modalsState,
                      showShareViaEmailReportModal: false,
                    })
                  );
                  // Show appropriate toast message
                  if (shouldShowEarlyTimeMessage()) {
                    toast.info(t("toast.reports_will_be_shared_soon"), {
                      autoClose: 10000,
                    });
                  } else {
                    toast.success(t("toast.create_success"), {
                      autoClose: 10000,
                    });
                  }

                  // re-fetch the reports and update the store
                  refetchCustomReportListToStore();
                } else {
                  toast.error(t("toast.create_failed"));
                }
              })
              .catch((error) => {
                const errors = !!error?.data
                  ? serializeErrorKeyValues(error?.data)
                  : [t("toast.create_failed")];
                toast.error(errors?.join(" "));
              })
              .finally(() => {
                setSubmitting(false);
              });
          }}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
            dirty,
            setFieldValue,
            setFieldTouched,
            resetForm,
            submitForm,
          }) => {
            return (
              <form
                className="p-8 bg-gray-100 grid grid-cols-6 gap-6 rounded-3xl shadow-2xl"
                onSubmit={handleSubmit}
              >
                <h2 className="col-span-6 text-2xl font-bold text-heading-black">
                  {isEdit ? t("heading_edit") : t("heading")}
                </h2>
                <div className="col-span-6 grid grid-cols-6 gap-6">
                  <div className="col-span-6 grid grid-cols-6 gap-6">
                    <AdminFormFieldInput
                      label={t("add_email_to_add")}
                      type="text"
                      id="email"
                      name="email"
                      onChange={handleChange}
                      // onBlur={handleBlur}
                      value={values.email}
                      error={errors.email}
                      touched={touched.email}
                      customWrapperClass="col-span-5"
                    />
                    <div
                      className={`w-36 flex ${!!errors.email ? "items-center" : "items-end"}`}
                    >
                      <AdminFormFieldSubmit
                        label={tMain("add")}
                        variant="primary"
                        type="button"
                        onClick={() => {
                          if (!values.selected_emails.includes(values.email)) {
                            setFieldValue("selected_emails", [
                              ...values.selected_emails,
                              values.email,
                            ]);
                          }
                          setFieldValue("email", "");
                        }}
                        disabled={!values.email || !!errors.email}
                      />
                    </div>
                  </div>
                  <AdminFormFieldPillList>
                    {values.selected_emails.map((email: string, i: number) => (
                      <AdminFormFieldPillItem // TODO: Fix prefill on change item from left column
                        key={`pillItem_${i}`}
                        item={{ name: email } as PillItem}
                        onRemove={() => {
                          setFieldValue(
                            "selected_emails",
                            values.selected_emails.filter(
                              (item: string) => item !== email
                            )
                          );
                        }}
                        disabled={false}
                      />
                    ))}
                  </AdminFormFieldPillList>
                </div>
                <div className="col-span-6 grid grid-cols-12 items-center">
                  {allowsXls && (
                    <AdminFormFieldRadio
                      label={t("xls")}
                      name="download_type"
                      id="xls"
                      type="radio"
                      value={"xls"}
                      checked={values.download_type === "xls"}
                      onChange={(e) => {
                        setFieldValue("download_type", e.target.value);
                      }}
                      customWrapperClass="col-span-4"
                    />
                  )}
                  <AdminFormFieldRadio
                    label={t("csv")}
                    name="download_type"
                    id="csv"
                    type="radio"
                    value={"csv"}
                    checked={values.download_type === "csv"}
                    onChange={(e) => {
                      setFieldValue("download_type", e.target.value);
                    }}
                    customWrapperClass="col-span-4"
                  />
                  <AdminFormFieldRadio
                    label={
                      t("pdf") +
                      `${
                        columns.length - 1 >
                        APP_CONFIG.REPORTS.MAX_COLS_FOR_PDF_SHARE
                          ? "  (" + tReports("too_many_columns") + ")"
                          : ""
                      }`
                    }
                    name="download_type"
                    id="pdf"
                    type="radio"
                    value={"pdf"}
                    checked={values.download_type === "pdf"}
                    onChange={(e) => {
                      setFieldValue("download_type", e.target.value);
                    }}
                    disabled={
                      columns.length - 1 >
                      APP_CONFIG.REPORTS.MAX_COLS_FOR_PDF_SHARE
                    }
                    title={`${
                      columns.length - 1 >
                      APP_CONFIG.REPORTS.MAX_COLS_FOR_PDF_SHARE
                        ? tReports("pdf_unavailable_too_many_columns")
                        : ""
                    }`}
                    customWrapperClass="col-span-4"
                  />
                </div>

                <AdminFormFieldCheckbox
                  label={t("send_automatically")}
                  id="send_automatically"
                  type="checkbox"
                  name="send_automatically"
                  checked={values.send_automatically}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  customWrapperClass="col-span-6"
                />

                {values?.send_automatically && (
                  <div className="col-span-6 grid grid-cols-10">
                    <AdminFormFieldRadio
                      label={t("hourly")}
                      name="send_automatically_period"
                      id="hourly"
                      type="radio"
                      value={"hourly"}
                      checked={values.send_automatically_period === "hourly"}
                      onChange={(e) => {
                        setFieldValue(
                          "send_automatically_period",
                          e.target.value
                        );
                      }}
                      customWrapperClass="col-span-2"
                    />
                    <AdminFormFieldRadio
                      label={t("daily")}
                      name="send_automatically_period"
                      id="daily"
                      type="radio"
                      value={"daily"}
                      checked={values.send_automatically_period === "daily"}
                      onChange={(e) => {
                        setFieldValue(
                          "send_automatically_period",
                          e.target.value
                        );
                      }}
                      customWrapperClass="col-span-2"
                    />
                    <AdminFormFieldRadio
                      label={t("weekly")}
                      name="send_automatically_period"
                      id="weekly"
                      type="radio"
                      value={"weekly"}
                      checked={values.send_automatically_period === "weekly"}
                      onChange={(e) => {
                        setFieldValue(
                          "send_automatically_period",
                          e.target.value
                        );
                      }}
                      customWrapperClass="col-span-2"
                    />
                    <AdminFormFieldRadio
                      label={t("monthly")}
                      name="send_automatically_period"
                      id="monthly"
                      type="radio"
                      value={"monthly"}
                      checked={values.send_automatically_period === "monthly"}
                      onChange={(e) => {
                        setFieldValue(
                          "send_automatically_period",
                          e.target.value
                        );
                      }}
                      customWrapperClass="col-span-2"
                    />
                    <AdminFormFieldRadio
                      label={t("bi_monthly")}
                      name="send_automatically_period"
                      id="bi_monthly"
                      type="radio"
                      value={"bi_monthly"}
                      checked={
                        values.send_automatically_period === "bi_monthly"
                      }
                      onChange={(e) => {
                        setFieldValue(
                          "send_automatically_period",
                          e.target.value
                        );
                      }}
                      customWrapperClass="col-span-2"
                    />
                  </div>
                )}
                {values?.send_automatically &&
                  values.send_automatically_period !== "hourly" && (
                    <div className="col-span-6 grid grid-cols-12 gap-4">
                      {values.send_automatically_period === "weekly" && (
                        <AdminFormFieldDropdown
                          label={t("day_of_week")}
                          id="day_of_week"
                          name="day_of_week"
                          options={DAY_OF_WEEK_OPTIONS}
                          value={values.day_of_week}
                          customWrapperClass="col-span-6"
                          onChange={(e) => {
                            setFieldValue("day_of_week", e?.value);
                          }}
                          placeholder={t("select_a_day")}
                        />
                      )}
                      {(values.send_automatically_period === "monthly" ||
                        values.send_automatically_period === "bi_monthly") && (
                        <AdminFormFieldDropdown
                          label={t("day_of_month")}
                          id="day_of_month"
                          name="day_of_month"
                          options={DAY_OF_MONTH_OPTIONS}
                          value={values.day_of_month}
                          customWrapperClass="col-span-6"
                          onChange={(e) => {
                            setFieldValue("day_of_month", e?.value);
                          }}
                          placeholder={t("select_a_day")}
                        />
                      )}
                      {values.send_automatically_period === "bi_monthly" && (
                        <AdminFormFieldDropdown
                          label={t("day_of_month2")}
                          id="day_of_month2"
                          name="day_of_month2"
                          options={DAY_OF_MONTH_OPTIONS}
                          value={values.day_of_month2}
                          customWrapperClass="col-span-6"
                          onChange={(e) => {
                            setFieldValue("day_of_month2", e?.value);
                          }}
                          placeholder={t("select_a_day")}
                        />
                      )}
                      <AdminFormFieldInput
                        label={t("delay_time")}
                        id="delay_time"
                        type="time-picker"
                        name="delay_time"
                        value={
                          values.delay_time?.includes?.(":")
                            ? new Date(
                                new Date().setHours(
                                  ...(values.delay_time
                                    .split(":")
                                    .map(Number) as [number, number?, number?]),
                                  0
                                )
                              )
                            : values.delay_time
                        }
                        onDatepickerChange={(fieldName, value) => {
                          setFieldValue(fieldName, value);
                        }}
                        customWrapperClass={
                          values?.send_automatically_period === "daily" ||
                          values?.send_automatically_period === "bi_monthly"
                            ? "col-span-12"
                            : "col-span-6"
                        }
                      />
                    </div>
                  )}
                <AdminFormFieldSubmit
                  label={tMain("share")}
                  variant="primary"
                  disabled={isSubmitting || values.selected_emails.length === 0}
                />

                <AdminFormFieldSubmit
                  label={tMain("cancel")}
                  type="button"
                  variant="danger-transparent"
                  disabled={isSubmitting}
                  onClick={hideModal}
                />
              </form>
            );
          }}
        </Formik>
      </div>
    </div>
  );
};

export default ModalShareViaEmail;
